<template>
  <div class="tab-panel-content">
    <div v-for="cat in categoriesWithPush" :key="cat.key" class="card">
      <div class="card-title">
        <span class="cat-dot" :style="{ background: 'var(--cat-' + cat.key + ')' }"></span>
        {{ cat.name }}
      </div>
      <div class="cfg-grid">
        <div v-for="cfg in pushByCat(cat.key)" :key="cfg.code" class="cfg-item">
          <div class="cfg-head">
            <div class="left"><span class="cat-tag" :class="'cat-tag-' + cat.key">{{ cfg.name }}</span></div>
            <span class="cfg-code">{{ cfg.code }}</span>
          </div>
          <div class="cfg-row">
            <el-slider
              :class="'sl-' + cat.key"
              v-model="cfg.interval"
              :min="1"
              :max="180"
              :step="1"
              :marks="{ 10: '10m', 30: '30m', 60: '1h', 120: '2h' }"
              :format-tooltip="formatMinutesTooltip"
              :disabled="!canEditPushInterval"
            />
            <div class="cfg-val">{{ cfg.interval }}<span class="cfg-unit">min</span></div>
          </div>
          <div class="cfg-hint">默认 {{ cfg.defaultVal }} 分钟 | 每 {{ cfg.interval }} 分钟推送一次</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAlarmConfig } from '../useAlarmConfig'
import { checkPermi } from '@/utils/permission'
import { computed } from 'vue'

const { categoriesWithPush, pushByCat, formatMinutesTooltip } = useAlarmConfig()
const canEditPushInterval = computed(() => checkPermi(['ops:alarmConfig:pushInterval']))
</script>

<style scoped lang="scss">
.card {
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid #fff;
  box-shadow: var(--shadow-card);
  padding: 20px 24px;
  margin-bottom: 16px;
  backdrop-filter: blur(6px);
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cat-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 10px;
  color: #fff;
  font-weight: 500;
}

.cat-tag-power {
  background: var(--cat-power);
}

.cat-tag-comm {
  background: var(--cat-comm);
}

.cat-tag-volt {
  background: var(--cat-volt);
}

.cat-tag-temp {
  background: var(--cat-temp);
}

.cat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cfg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 12px;
}

.cfg-item {
  background: color-mix(in srgb, #ffffff 88%, var(--primary-faint));
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px 20px;
  transition: box-shadow 0.2s ease;
}

.cfg-item:hover {
  box-shadow: var(--shadow-card);
}

.cfg-item .cfg-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.cfg-item .cfg-head .left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cfg-item .cfg-code {
  font-size: 11px;
  color: var(--text-muted);
  font-family: monospace;
}

.cfg-item .cfg-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cfg-item .cfg-row :deep(.el-slider) {
  flex: 1;
}

.cfg-item .cfg-val {
  min-width: 72px;
  text-align: right;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.cfg-item .cfg-unit {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 400;
}

.cfg-item .cfg-hint {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 18px;
}

.sl-power :deep(.el-slider__bar),
.sl-power :deep(.el-slider__button) {
  background: var(--cat-power);
  border-color: var(--cat-power);
}

.sl-comm :deep(.el-slider__bar),
.sl-comm :deep(.el-slider__button) {
  background: var(--cat-comm);
  border-color: var(--cat-comm);
}

.sl-volt :deep(.el-slider__bar),
.sl-volt :deep(.el-slider__button) {
  background: var(--cat-volt);
  border-color: var(--cat-volt);
}

.sl-temp :deep(.el-slider__bar),
.sl-temp :deep(.el-slider__button) {
  background: var(--cat-temp);
  border-color: var(--cat-temp);
}

@media (prefers-reduced-motion: reduce) {
  .cfg-item {
    transition: none;
  }
}
</style>
